import React, { useEffect, useRef, useState } from "react";
import { PlxsButton, PlxsText, PlxsAlert } from "@plexlogic/plexus-ui";
import { DrawData } from "../../types/DrawData";
import { useAuth } from "../../contexts/AuthContext";
import { setWizardSession } from "../../services/wizardStorage";
import WizardSpinner from "../WizardSpinner";
import apiService, { SubmitDrawRequest } from "../../services/api";
import useUploadStore from "../uploadEntries/useUploadStore";

interface ScheduledConfirmationStepProps {
  drawData: DrawData;
  currentStep: number;
  onLocked?: () => void;
}

const formatScheduledDateTime = (value: Date | string): string => {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const weekday = d.toLocaleDateString("en-AU", { weekday: "short" });
  const month = d.toLocaleDateString("en-AU", { month: "long" });
  const day = d.getDate();
  const rawHours = d.getHours();
  const minutes = d.getMinutes();
  const ampm = rawHours >= 12 ? "pm" : "am";
  const hours12 = rawHours % 12 || 12;
  const time = `${hours12}:${String(minutes).padStart(2, "0")}${ampm}`;
  return `${weekday} ${day} ${month}, ${time}`;
};

const buildScheduleRequest = (
  drawData: DrawData,
  csvSettings: ReturnType<
    typeof useUploadStore.getState
  >["csvConfirmationSettings"]
): SubmitDrawRequest => ({
  draw_name: drawData.drawName,
  organisation: drawData.organisation,
  entry_method: drawData.entryMethod,
  entry_count: drawData.entryCount,
  prizes: drawData.prizes.map((prize) => ({
    description: prize.description,
    winners: prize.winners,
    reserves: prize.reserves || 0,
    estimated_value: prize.estimatedValue || 0,
  })),
  csv_confirmation_setting: csvSettings
    ? {
        hasHeader: csvSettings.isHeaderRow,
        ticketCountColumn: csvSettings.ticketCountColumn,
        participantIdentificationColumn:
          csvSettings.participantIdentificationColumn,
      }
    : undefined,
  total_prize_pool_value: drawData.totalPrizePoolValue,
  scrutineer_settings:
    drawData.scrutineerSettings?.scrutineerRequired !== undefined
      ? {
          scrutineer_required: drawData.scrutineerSettings.scrutineerRequired,
          scrutineer_name: drawData.scrutineerSettings.scrutineerName,
          scrutineer_email: drawData.scrutineerSettings.scrutineerEmail,
          scrutineer_job_title: drawData.scrutineerSettings.scrutineerJobTitle,
          scrutineer_confirmation:
            drawData.scrutineerSettings.scrutineerConfirmation,
        }
      : undefined,
  max_prizes_per_entrant: drawData.maxPrizesPerEntrant,
  scheduled_date_time: drawData.scheduledDateTime?.toISOString(),
  status: "scheduled",
});

const ScheduledConfirmationStep: React.FC<ScheduledConfirmationStepProps> = ({
  drawData,
  currentStep,
  onLocked,
}) => {
  const { loginWithRedirect } = useAuth();

  type CommitState = "committing" | "success" | "error";
  const [commitState, setCommitState] = useState<CommitState>("committing");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [attempt, setAttempt] = useState(0);

  const inflightRef = useRef(false);

  useEffect(() => {
    if (!drawData.drawId) {
      setCommitState("error");
      setErrorMessage(
        "Draw ID missing — please go back to the setup step and try again."
      );
      return;
    }

    if (inflightRef.current) return;
    inflightRef.current = true;

    const isAlreadyScheduledError = (err: any): boolean =>
      err?.status === 400 &&
      typeof err?.message === "string" &&
      /only draft draws can be updated/i.test(err.message);

    const commitSchedule = async () => {
      try {
        const csvSettings = useUploadStore.getState().csvConfirmationSettings;
        await apiService.updateDraw(
          drawData.drawId!,
          buildScheduleRequest(drawData, csvSettings),
          { skipNotificationFor: isAlreadyScheduledError }
        );
        setCommitState("success");
        onLocked?.();
      } catch (err: any) {
        if (isAlreadyScheduledError(err)) {
          setCommitState("success");
          onLocked?.();
          return;
        }
        setCommitState("error");
        setErrorMessage(
          typeof err?.message === "string" && err.message
            ? err.message
            : "Couldn't schedule your draw. Please try again."
        );
      } finally {
        inflightRef.current = false;
      }
    };

    commitSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  const handleRetry = () => {
    setErrorMessage("");
    setCommitState("committing");
    setAttempt((n) => n + 1);
  };

  const handleLogin = () => {
    setWizardSession(drawData, currentStep);
    sessionStorage.setItem("isRedirectingToLogin", "true");
    loginWithRedirect();
  };

  const scheduledText = drawData.scheduledDateTime
    ? formatScheduledDateTime(drawData.scheduledDateTime as Date)
    : "";

  if (commitState === "committing") {
    return (
      <div
        data-testid="scheduled-confirmation-step"
        style={{ textAlign: "center", padding: "3rem 0" }}
      >
        <WizardSpinner size={40} dataTestId="schedule-committing-spinner" />
        <div style={{ marginTop: "var(--plexus-space-md)" }}>
          <PlxsText size="sm">Scheduling your draw…</PlxsText>
        </div>
      </div>
    );
  }

  if (commitState === "error") {
    return (
      <div
        data-testid="scheduled-confirmation-step"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          padding: "var(--plexus-space-xl) var(--plexus-space-lg)",
        }}
      >
        <div style={{ width: "100%", maxWidth: 560, marginBottom: "var(--plexus-space-lg)" }}>
          <PlxsAlert variant="error" dataTestId="schedule-error-alert">
            {errorMessage}
          </PlxsAlert>
        </div>
        <PlxsButton
          dataTestId="schedule-retry-button"
          label="Retry"
          variant="primary"
          tone="brand"
          onClick={handleRetry}
        />
      </div>
    );
  }

  return (
    <div
      data-testid="scheduled-confirmation-step"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        padding: "var(--plexus-space-xl) var(--plexus-space-lg)",
      }}
    >
      <div
        data-testid="scheduled-confirmation-heading"
        style={{
          fontSize: "1.5rem",
          fontWeight: 600,
          marginBottom: "var(--plexus-space-md)",
          color: "var(--plexus-color-neutral-100)",
          lineHeight: 1.3,
          maxWidth: 560,
        }}
      >
        {scheduledText
          ? `Your draw runs on ${scheduledText}.`
          : "Your draw is set."}
      </div>
      <div
        style={{
          marginBottom: "var(--plexus-space-lg)",
          color: "var(--plexus-color-neutral-200)",
          maxWidth: 480,
        }}
      >
        <PlxsText size="sm">
          Create a free account to follow your draw. Once it runs, you can
          purchase your results – winners plus an audit-ready certificate.
        </PlxsText>
      </div>
      <PlxsButton
        dataTestId="scheduled-confirmation-cta"
        label="Login or create my account"
        variant="primary"
        tone="brand"
        onClick={handleLogin}
      />
    </div>
  );
};

export default ScheduledConfirmationStep;
